/**
 * Utility for transforming bill data from API responses
 * Handles data enrichment and extraction
 * Note: Date and currency formatting is handled by FormatPipe in the UI layer
 */
export class BillDataTransformer {
  /**
   * Transform a raw bill item from the API into enriched bill data
   * Converts date strings to Date objects for proper sorting
   * Adds claim information and provides fallback values
   */
  static transformBillItem(
    bill: any,
    index: number,
    claimInfo: {
      claimNumber?: string;
      fileNumber?: string;
      claimantFirstName?: string;
      claimantLastName?: string;
      accountName?: string;
    },
  ): any {
    return {
      ...bill,
      row: index,
      // Convert date strings to Date objects for proper sorting
      // FormatPipe will handle the display formatting
      rcvdSedgwick: this.parseToDate(bill.rcvdSedgwick),
      rcvdJuris: this.parseToDate(bill.rcvdJuris),
      dosBegin: this.parseToDate(bill.dosBegin),
      dosEnd: this.parseToDate(bill.dosEnd),
      providerPaid: this.parseToDate(bill.providerPaid),
      // Convert currency strings to numbers for proper sorting
      billedAmount: this.parseToNumber(bill.billedAmount),
      recAmount: this.parseToNumber(bill.recAmount),
      // Provide fallback for empty payee name
      payee: bill.payee || 'N/A',
      // Add claim information for export
      claimNumber: claimInfo.claimNumber || claimInfo.fileNumber || '',
      claimantName:
        `${claimInfo.claimantFirstName || ''} ${claimInfo.claimantLastName || ''}`.trim(),
      contractName: bill.contractName || claimInfo.accountName || '',
    };
  }

  /**
   * Transform array of bills from API response
   */
  static transformBills(
    billsArray: any[],
    claimInfo: {
      claimNumber?: string;
      fileNumber?: string;
      claimantFirstName?: string;
      claimantLastName?: string;
      accountName?: string;
    },
  ): any[] {
    if (!billsArray || !Array.isArray(billsArray)) {
      return [];
    }

    return billsArray.map((bill, index) =>
      this.transformBillItem(bill, index, claimInfo),
    );
  }

  /**
   * Extract bills array from API response (handles both uppercase and lowercase property names)
   */
  static extractBillsArray(billsResponse: any): any[] {
    return billsResponse?.Bills || billsResponse?.bills || [];
  }

  /**
   * Sort bills by rcvdSedgwick date in descending order (newest first)
   * Handles MM/DD/YYYY format from API
   */
  static sortByRcvdSedgwick(bills: any[]): any[] {
    return bills.sort((a, b) => {
      const dateA = a.rcvdSedgwick ? this.parseDateForSort(a.rcvdSedgwick) : 0;
      const dateB = b.rcvdSedgwick ? this.parseDateForSort(b.rcvdSedgwick) : 0;
      return dateB - dateA; // Descending order
    });
  }

  /**
   * Parse date string to Date object
   * Handles MM/DD/YYYY format (with or without time), ISO strings, and Date objects
   * Returns null for invalid/empty dates
   */
  private static parseToDate(dateValue: any): Date | null {
    if (!dateValue || dateValue === '') return null;

    if (dateValue instanceof Date) {
      return dateValue;
    }

    if (typeof dateValue === 'string') {
      // Handle MM/DD/YYYY format (with or without time like "2/26/2020 12:00:00 AM")
      if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(dateValue)) {
        // Extract just the date portion before any space (time portion)
        const datePortion = dateValue.split(' ')[0];
        const parts = datePortion.split('/');
        const month = parseInt(parts[0], 10) - 1;
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        return new Date(year, month, day);
      }
      // Handle ISO strings
      const date = new Date(dateValue);
      return isNaN(date.getTime()) ? null : date;
    }

    return null;
  }

  /**
   * Parse date value for sorting
   * Handles Date objects, ISO strings, and MM/DD/YYYY format
   */
  private static parseDateForSort(dateValue: any): number {
    const date = this.parseToDate(dateValue);
    return date ? date.getTime() : 0;
  }

  /**
   * Parse currency/number string to number for proper sorting
   * Handles formats like "$1,234.56", "1,234.56", "1234.56", or already numeric values
   * Returns null for invalid/empty values
   */
  private static parseToNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;

    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      // Remove currency symbols, commas, and whitespace
      const cleaned = value.replace(/[$,\s]/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? null : parsed;
    }

    return null;
  }

  /**
   * Extract bill lines from BillLinesResponse
   * Handles both lowercase and uppercase property names from API
   */
  static getBillLines(billLinesData: any): any[] {
    const data = billLinesData as any;
    const lines = data?.billLines || data?.BillLines || [];
    return lines;
  }

  /**
   * Transform bill lines to ensure proper data types for sorting
   * Converts lineNum strings to numbers for proper numeric sorting
   */
  static transformBillLines(billLines: any[]): any[] {
    if (!billLines || !Array.isArray(billLines)) {
      return [];
    }

    return billLines.map((line: any) => ({
      ...line,
      lineNum: this.parseToNumber(line.lineNum) ?? line.lineNum,
    }));
  }

  /**
   * Enrich bill lines with claim and bill information for export
   * @param billLines Array of bill lines to enrich
   * @param claimInfo Claim information to include
   * @param billInfo Bill information to include
   * @returns Enriched bill lines with additional context
   */
  static enrichBillLinesForExport(
    billLines: any[],
    claimInfo: {
      claimNumber?: string;
      fileNumber?: string;
      claimantFirstName?: string;
      claimantLastName?: string;
      accountName?: string;
    },
    billInfo: {
      contractNumber?: string;
      contractName?: string;
      icn?: string;
      invoice?: string;
    },
  ): any[] {
    return billLines.map((line: any) => ({
      ...line,
      claimNumber: claimInfo.claimNumber || claimInfo.fileNumber || '',
      claimantName:
        `${claimInfo.claimantFirstName || ''} ${claimInfo.claimantLastName || ''}`.trim(),
      contractNumber: billInfo.contractNumber || '',
      contractName: billInfo.contractName || claimInfo.accountName || '',
      icn: billInfo.icn || '',
      invoice: billInfo.invoice || '',
    }));
  }
}
